import React, { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import { Col, Nav, NavItem, NavLink, Row, TabContent, TabPane } from "reactstrap"
import classnames from "classnames"
import Select from "react-select"
import { BMD_COLOURS } from "../../bmd/constants/bmd.colours"
import { BmdStrandPicker, strandKey } from "../../bmd/components/BmdStrandPicker"
import type { BmdStagedDraft, BmdTopic } from "../../bmd/types/bmd.types"
import { useEngDiagStrands } from "../hooks/useEngDiagStrands"
import {
  getEngDiagYearLevels,
  type EngDiagYearLevel,
} from "../services/eng-diag.service"
import { EngDiagPreviewModal } from "./EngDiagPreviewModal"
import {
  EngDiagPrereqTabPanel,
  type EngDiagPrereqStagedDraft,
} from "./EngDiagPrereqTabPanel"

type Props = {
  subjectId: number
  studentKey: string
  /** Hands the selection to the page's Assigned Diagnostics list; nothing is saved until Add Activities. */
  onStageDraft: (draft: BmdStagedDraft) => void
  /** Prerequisite tests already staged in this visit, so one is not queued twice. */
  prereqStagedKeys?: string[]
  onStagePrereqDraft?: (draft: EngDiagPrereqStagedDraft) => void
}

/** Which kind of activity the panel is assigning. Mirrors the maths panel's pills. */
type EngDiagAssignMode = "test" | "prereq"

type YearOption = { value: number; label: string }

// Same tokens as BmdTabPanel, so the two Diagnostics tabs read as one feature.
const INK = BMD_COLOURS.INK
const BODY = BMD_COLOURS.BODY
const MUTED = BMD_COLOURS.MUTED
const RULE = BMD_COLOURS.RULE
const ACCENT = BMD_COLOURS.ACCENT

const CARD: React.CSSProperties = {
  backgroundColor: "#fff",
  border: "1px solid #e9edf3",
  borderRadius: 10,
  padding: 20,
}

const LABEL: React.CSSProperties = {
  color: INK,
  fontSize: 12.5,
  fontWeight: 700,
  marginBottom: 8,
  display: "block",
}

const HELPER: React.CSSProperties = {
  color: MUTED,
  fontSize: 11.5,
  lineHeight: 1.5,
}

const SELECT_STYLES = {
  menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 9999 }),
  control: (base: Record<string, unknown>) => ({
    ...base,
    minHeight: "40px",
    fontSize: "13px",
  }),
}

const OUTLINE_BUTTON = (enabled: boolean): React.CSSProperties => ({
  backgroundColor: "#fff",
  border: `1px solid ${enabled ? ACCENT : "#e4e7ee"}`,
  color: enabled ? ACCENT : MUTED,
  borderRadius: 6,
  padding: "9px 18px",
  fontSize: 13,
  fontWeight: 600,
  whiteSpace: "nowrap",
})

const SOLID_BUTTON = (enabled: boolean): React.CSSProperties => ({
  backgroundColor: enabled ? ACCENT : "#e9ecf1",
  border: `1px solid ${enabled ? ACCENT : "#e9ecf1"}`,
  color: enabled ? "#fff" : MUTED,
  borderRadius: 6,
  padding: "9px 18px",
  fontSize: 13,
  fontWeight: 700,
})

/** "2 clusters" - the number bold, the noun not. */
const Tally: React.FC<{ value: number; noun: string }> = ({ value, noun }) => (
  <span style={{ color: BODY, fontSize: 12.5 }}>
    <strong style={{ color: INK }}>{value}</strong> {noun}
    {value === 1 ? "" : "s"}
  </span>
)

/**
 * English Diagnostics assign tab: pick a year, tick clusters, add them to the
 * page's Assigned Diagnostics list. Same flow and chrome as the math BMD tab,
 * and the same cluster picker: the API returns English clusters in the shape
 * the picker already takes.
 *
 * Clusters unlock in curriculum order. The first is always open; each later
 * one opens once everything before it is either already assigned to the
 * student or ticked in this selection.
 */
export function EngDiagAssignPanel({
  subjectId,
  studentKey,
  onStageDraft,
  prereqStagedKeys = [],
  onStagePrereqDraft,
}: Props) {
  const [mode, setMode] = useState<EngDiagAssignMode>("test")

  // Lifted verbatim from BmdAssignPanel. An inactive pill needs the border and white fill or it
  // reads as a text link rather than the other half of a toggle, which is how this looked
  // against the maths page.
  const pillStyle = (isActive: boolean) => ({
    cursor: "pointer",
    border: `1px solid ${isActive ? "transparent" : "#d5d8e2"}`,
    backgroundColor: isActive ? undefined : "#ffffff",
    color: isActive ? undefined : "#5b6270",
    fontWeight: 600,
  })
  const [yearLevels, setYearLevels] = useState<EngDiagYearLevel[]>([])
  const [yearsLoading, setYearsLoading] = useState(false)
  const [yearsError, setYearsError] = useState<string | null>(null)
  const [selectedYear, setSelectedYear] = useState<YearOption | null>(null)
  const [selectedStrandKeys, setSelectedStrandKeys] = useState<Set<number>>(
    new Set(),
  )
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewTopicIds, setPreviewTopicIds] = useState<number[]>([])

  useEffect(() => {
    const controller = new AbortController()
    ;(async () => {
      try {
        setYearsLoading(true)
        setYearsError(null)
        setSelectedYear(null)
        setSelectedStrandKeys(new Set())
        const data = await getEngDiagYearLevels(
          { subject_id: subjectId, student_key: studentKey },
          controller.signal,
        )
        setYearLevels(data.year_levels || [])
      } catch (e: any) {
        if (controller.signal.aborted) return
        setYearsError(e?.message || "Unable to load year levels.")
        setYearLevels([])
      } finally {
        if (!controller.signal.aborted) setYearsLoading(false)
      }
    })()
    return () => controller.abort()
  }, [subjectId, studentKey])

  const yearOptions = useMemo(
    () =>
      yearLevels.map(y => ({
        value: y.year_level_id,
        label: y.level_name,
      })),
    [yearLevels],
  )

  const {
    strands,
    lockedTopicIds,
    loading: strandsLoading,
    error: strandsError,
  } = useEngDiagStrands(selectedYear?.value ?? 0, studentKey)

  const orderedStrands = useMemo(
    () => [...strands].sort((a, b) => a.order - b.order),
    [strands],
  )

  const unlockedStrandKeys = useMemo(() => {
    const unlocked = new Set<number>()
    let prefixOk = true
    orderedStrands.forEach((strand, index) => {
      const key = strandKey(strand)
      if (index === 0 || prefixOk || strand.previously_assigned) {
        unlocked.add(key)
      }
      prefixOk =
        prefixOk &&
        (strand.previously_assigned || selectedStrandKeys.has(key))
    })
    return unlocked
  }, [orderedStrands, selectedStrandKeys])

  /** Names the cluster that has to come first, for the locked-row tooltip. */
  const previousStrandNameByKey = useMemo(() => {
    const map = new Map<number, string>()
    for (let i = 1; i < orderedStrands.length; i++) {
      map.set(strandKey(orderedStrands[i]), orderedStrands[i - 1].strand_name)
    }
    return map
  }, [orderedStrands])

  // Locked ones stay out of "select all": the unlock order is the point of them.
  const selectableStrandKeys = useMemo(
    () =>
      orderedStrands
        .filter(strand => {
          const key = strandKey(strand)
          if (!unlockedStrandKeys.has(key)) return false
          return strand.topics.some(t => !lockedTopicIds.has(t.main_topic_id))
        })
        .map(strandKey),
    [orderedStrands, unlockedStrandKeys, lockedTopicIds],
  )

  const allAvailableSelected =
    selectableStrandKeys.length > 0 &&
    selectableStrandKeys.every(k => selectedStrandKeys.has(k))

  // What the draft will contain: the ticked clusters' topics, minus any the
  // student already has at this year.
  const selectedTopics: BmdTopic[] = useMemo(() => {
    const out: BmdTopic[] = []
    const seen = new Set<number>()
    for (const strand of orderedStrands) {
      if (!selectedStrandKeys.has(strandKey(strand))) continue
      for (const topic of strand.topics) {
        if (lockedTopicIds.has(topic.main_topic_id)) continue
        if (seen.has(topic.main_topic_id)) continue
        seen.add(topic.main_topic_id)
        out.push(topic)
      }
    }
    return out
  }, [orderedStrands, selectedStrandKeys, lockedTopicIds])

  const selectedStrandNames = useMemo(
    () =>
      orderedStrands
        .filter(s => selectedStrandKeys.has(strandKey(s)))
        .map(s => s.strand_name),
    [orderedStrands, selectedStrandKeys],
  )

  const questionCount = selectedTopics.reduce(
    (sum, t) => sum + t.question_count,
    0,
  )
  const canStage = !!selectedYear && selectedTopics.length > 0

  const handleYearChange = (option: YearOption | null) => {
    setSelectedYear(option)
    setSelectedStrandKeys(new Set())
  }

  const handleToggleStrand = (key: number) => {
    setSelectedStrandKeys(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const toggleSelectAllAvailable = () => {
    setSelectedStrandKeys(
      allAvailableSelected ? new Set() : new Set(selectableStrandKeys),
    )
  }

  const openPreview = (topicIds: number[]) => {
    setPreviewTopicIds(topicIds)
    setPreviewOpen(true)
  }

  const handleStageDraft = () => {
    if (!selectedYear || selectedTopics.length === 0) return
    onStageDraft({
      draft_id:
        Date.now().toString(36) + Math.random().toString(36).slice(2, 8),
      subject_id: subjectId,
      year_level_id: selectedYear.value,
      year_level_label: selectedYear.label,
      topic_ids: selectedTopics.map(t => t.main_topic_id),
      topic_names: selectedTopics.map(t => t.display_name),
      topic_count: selectedTopics.length,
      question_count: questionCount,
      force_new: false,
      strand_names: selectedStrandNames,
    })
    // Names where it went: the page's Add Activities button is still to come.
    toast.success(
      `Added to Assigned Diagnostics: ${selectedStrandNames.length} cluster${
        selectedStrandNames.length === 1 ? "" : "s"
      } · ${questionCount} question${questionCount === 1 ? "" : "s"}`,
      { autoClose: 2500 },
    )
    setSelectedYear(null)
    setSelectedStrandKeys(new Set())
  }

  return (
    <div style={{ padding: "10px 16px" }}>
      {/* Two kinds of activity behind one panel, the same shape as the maths one: a diagnostic
          the tutor chooses, and a prerequisite test built from one the student already sat. */}
      {onStagePrereqDraft ? (
        <Nav pills className="gap-2" style={{ marginBottom: 10 }}>
          <NavItem>
            <NavLink
              style={pillStyle(mode === "test")}
              className={classnames("font-size-13", { active: mode === "test" })}
              onClick={() => setMode("test")}
            >
              Diagnostics Test
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              style={pillStyle(mode === "prereq")}
              className={classnames("font-size-13", { active: mode === "prereq" })}
              onClick={() => setMode("prereq")}
            >
              Prerequisite Test
            </NavLink>
          </NavItem>
        </Nav>
      ) : null}

      {onStagePrereqDraft && mode === "prereq" ? (
        <EngDiagPrereqTabPanel
          studentKey={studentKey}
          stagedKeys={prereqStagedKeys}
          onStageDraft={onStagePrereqDraft}
        />
      ) : (
      <>
      {yearsError ? (
        <div className="alert alert-warning font-size-12 py-2">
          {yearsError}
        </div>
      ) : null}

      <div style={{ ...CARD, marginBottom: 16 }}>
        <Row className="align-items-start">
          <Col md={4}>
            <label style={LABEL} htmlFor="eng-diag-year-level">
              Select a diagnostics year level
            </label>
            <Select<YearOption, false>
              inputId="eng-diag-year-level"
              value={selectedYear}
              onChange={handleYearChange}
              options={yearOptions}
              isLoading={yearsLoading}
              isDisabled={yearsLoading || yearOptions.length === 0}
              placeholder="Select year level"
              classNamePrefix="select2-selection"
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : null
              }
              menuPosition="fixed"
              styles={SELECT_STYLES}
            />
            <div style={{ ...HELPER, marginTop: 8 }}>
              {!yearsLoading && yearOptions.length === 0
                ? "No year levels available for this student."
                : "Choose a year level, then tick the clusters to assess."}
            </div>
          </Col>

          <Col
            md={8}
            className="mt-3 mt-md-0 d-flex justify-content-md-end align-items-start"
          >
            <button
              type="button"
              className="btn d-inline-flex align-items-center gap-2"
              style={OUTLINE_BUTTON(!!selectedYear)}
              onClick={() => openPreview([])}
              disabled={!selectedYear}
            >
              <i className="mdi mdi-eye-outline" style={{ fontSize: 15 }}></i>
              Preview all {selectedYear ? selectedYear.label : "year"} questions
            </button>
          </Col>
        </Row>
      </div>

      {selectedYear ? (
        <div style={{ ...CARD, padding: 0, marginBottom: 16 }}>
          <div
            className="d-flex align-items-center justify-content-between"
            style={{ padding: "16px 14px 12px" }}
          >
            <span style={{ color: INK, fontSize: 15, fontWeight: 700 }}>
              Clusters for {selectedYear.label}
            </span>
            <div className="d-flex align-items-center gap-3">
              <button
                type="button"
                className="btn btn-link p-0"
                style={{ color: ACCENT, fontSize: 12.5, textDecoration: "none" }}
                onClick={toggleSelectAllAvailable}
                disabled={selectableStrandKeys.length === 0}
              >
                {allAvailableSelected ? "Clear all" : "Select all available"}
              </button>
              <button
                type="button"
                className="btn"
                style={{ ...SOLID_BUTTON(canStage), padding: "7px 16px", fontSize: 12.5 }}
                disabled={!canStage}
                onClick={handleStageDraft}
              >
                Add to assignment
              </button>
            </div>
          </div>

          {strandsLoading ? (
            <p className="text-muted mb-0 font-size-12 px-3 pb-3">
              Loading clusters…
            </p>
          ) : null}

          {strandsError && !strandsLoading ? (
            <p className="text-danger mb-0 font-size-12 px-3 pb-3">
              Could not load clusters: {strandsError}
            </p>
          ) : null}

          {!strandsLoading && !strandsError ? (
            <BmdStrandPicker
              strands={orderedStrands}
              selectedStrandKeys={selectedStrandKeys}
              unlockedStrandKeys={unlockedStrandKeys}
              lockedTopicIds={lockedTopicIds}
              previousStrandNameByKey={previousStrandNameByKey}
              onToggleStrand={handleToggleStrand}
              onPreviewStrand={(_key, topicIds) => openPreview(topicIds)}
            />
          ) : null}
        </div>
      ) : null}

      {selectedYear && selectedTopics.length > 0 ? (
        <div style={{ ...CARD, marginBottom: 16 }}>
          <div className="d-flex align-items-center flex-wrap gap-3">
            <span style={{ color: INK, fontSize: 13, fontWeight: 700 }}>
              {selectedYear.label}
            </span>
            <span style={{ width: 1, height: 18, backgroundColor: RULE }} />
            <Tally value={selectedStrandNames.length} noun="cluster" />
            <Tally value={selectedTopics.length} noun="topic" />
            <Tally value={questionCount} noun="question" />

            <div className="d-flex align-items-center gap-2 ms-auto">
              <button
                type="button"
                className="btn btn-link p-0"
                style={{ color: MUTED, fontSize: 12.5, textDecoration: "none" }}
                onClick={() => setSelectedStrandKeys(new Set())}
              >
                Clear
              </button>
              <button
                type="button"
                className="btn"
                style={OUTLINE_BUTTON(true)}
                onClick={() =>
                  openPreview(selectedTopics.map(t => t.main_topic_id))
                }
              >
                Preview selection
              </button>
              <button
                type="button"
                className="btn"
                style={SOLID_BUTTON(true)}
                onClick={handleStageDraft}
              >
                Add to assignment
              </button>
            </div>
          </div>

          <div style={{ ...HELPER, marginTop: 10, textAlign: "right" }}>
            Added clusters appear under <strong>Assigned Diagnostics</strong> on
            the right, and are confirmed with <strong>Add Activities</strong>.
          </div>
        </div>
      ) : null}

      {selectedYear ? (
        <EngDiagPreviewModal
          isOpen={previewOpen}
          onClose={() => setPreviewOpen(false)}
          yearLevelId={selectedYear.value}
          yearLevelLabel={selectedYear.label}
          subjectId={subjectId}
          topicIds={previewTopicIds}
        />
      ) : null}
      </>
      )}
    </div>
  )
}

export default EngDiagAssignPanel
